package co.maytes.standupbot.dashboard.features.onboarding

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.layout.ShellGround
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.AnimatedCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun WizardStage(content: @Composable () -> Unit) =
    AppShell(hideNav = true, ground = ShellGround.Canvas) { content() }

@Composable
fun WizardStepper(current: Int) {
    val steps = OnboardingStep.ordered
    val total = steps.size
    Div(
        attrs = {
            classes("sb-wiz-stepper")
            style { property("--wiz-n", total.toString()) }
        },
    ) {
        Div(attrs = { classes("sb-wiz-track"); attr("aria-hidden", "true") }) {
            Div(
                attrs = {
                    classes("sb-wiz-track-fill")
                    style {
                        property("--wiz-from", WizardProgress.fillFrom(current, total).toString())
                        property("--wiz-to", WizardProgress.fillTo(current, total).toString())
                    }
                },
            )
        }
        Ol(attrs = { classes("sb-wiz-steps"); attr("aria-label", "Setup progress") }) {
            steps.forEachIndexed { index, step ->
                val state = WizardProgress.nodeState(index, current)
                Li(
                    attrs = {
                        classes("sb-wiz-step")
                        if (state == NodeState.Current) attr("aria-current", "step")
                    },
                ) {
                    Div(
                        attrs = {
                            classes("sb-wiz-node")
                            when (state) {
                                NodeState.Done -> classes("sb-wiz-node--done")
                                NodeState.Current -> classes("sb-wiz-node--current")
                                NodeState.Upcoming -> Unit
                            }
                        },
                    ) {
                        when {
                            state != NodeState.Done -> Text((index + 1).toString())
                            WizardProgress.justCompleted(index, current) -> AnimatedCheck(sizePx = 14)
                            else -> LucideCheck(size = 14)
                        }
                    }
                    Span(
                        attrs = {
                            classes("sb-wiz-step-label")
                            when (state) {
                                NodeState.Done -> classes("sb-wiz-step-label--done")
                                NodeState.Current -> classes("sb-wiz-step-label--current")
                                NodeState.Upcoming -> Unit
                            }
                        },
                    ) { Text(step.label) }
                }
            }
        }
    }
}

@Composable
fun WizardFrame(
    title: String,
    subline: String?,
    stepper: (@Composable () -> Unit)? = null,
    art: (@Composable () -> Unit)? = null,
    footerStart: (@Composable () -> Unit)? = null,
    footerEnd: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    Div(attrs = { classes("sb-wiz") }) {
        stepper?.invoke()
        Div(attrs = { classes("sb-wiz-panel") }) {
            art?.let { Div(attrs = { classes("sb-wiz-art") }) { it() } }
            Div(attrs = { classes("sb-wiz-body") }) {
                StateSwap(state = title to subline) {
                    Div(attrs = { classes("sb-wiz-head") }) {
                        H1(attrs = { classes("sb-wiz-title") }) { Text(title) }
                        subline?.let { P(attrs = { classes("sb-wiz-sub") }) { Text(it) } }
                    }
                }
                content()
            }
            Div(attrs = { classes("sb-wiz-footer") }) {
                Div(attrs = { classes("sb-wiz-footer-start") }) { footerStart?.invoke() }
                Div(attrs = { classes("sb-wiz-footer-end") }) { footerEnd() }
            }
        }
    }
}

@Composable
fun WizardSection(
    title: String,
    hint: String? = null,
    action: (@Composable () -> Unit)? = null,
    icon: @Composable () -> Unit,
    content: @Composable () -> Unit,
) {
    Div(attrs = { classes("sb-wiz-section") }) {
        Div(attrs = { classes("sb-wiz-section-head") }) {
            Div(attrs = { classes("sb-wiz-icon-chip") }) { icon() }
            Div(attrs = { classes("sb-wiz-section-text") }) {
                Span(attrs = { classes("sb-wiz-section-title") }) { Text(title) }
                hint?.let { Span(attrs = { classes("sb-wiz-section-hint") }) { Text(it) } }
            }
            action?.let { Div(attrs = { classes("sb-wiz-section-action") }) { it() } }
        }
        content()
    }
}

@Composable
fun OptionCard(
    title: String,
    description: String,
    selected: Boolean,
    onSelect: () -> Unit,
    leading: @Composable () -> Unit,
) {
    Button(
        attrs = Modifier
            .classNames("sb-wiz-opt")
            .let { if (selected) it.classNames("sb-wiz-opt--selected") else it }
            .onClick { onSelect() }
            .toAttrs {
                attr("type", "button")
                attr("aria-pressed", selected.toString())
            },
    ) {
        Div(attrs = { classes("sb-wiz-opt-lead") }) { leading() }
        Div(attrs = { classes("sb-wiz-opt-text") }) {
            Span(attrs = { classes("sb-wiz-opt-title") }) { Text(title) }
            Span(attrs = { classes("sb-wiz-opt-desc") }) { Text(description) }
        }
        Div(attrs = { classes("sb-wiz-opt-mark") }) { if (selected) AnimatedCheck(sizePx = 12) }
    }
}

enum class ConnectionState { Checking, Disconnected, Connected }

@Composable
fun ConnectionCard(brand: WizardBrand, title: String, detail: String, state: ConnectionState) {
    Div(
        attrs = {
            classes("sb-wiz-conn")
            if (state == ConnectionState.Connected) classes("sb-wiz-conn--live")
        },
    ) {
        BrandChip(brand = brand, size = ChipSize.Md)
        Div(attrs = { classes("sb-wiz-conn-text") }) {
            Span(attrs = { classes("sb-wiz-conn-title") }) { Text(title) }
            Span(attrs = { classes("sb-wiz-conn-detail") }) { Text(detail) }
        }
        StateSwap(state = state) {
            when (state) {
                ConnectionState.Checking -> Div(attrs = { classes("sb-wiz-bar"); attr("aria-hidden", "true") })
                ConnectionState.Disconnected -> StatusChip(label = "Not connected", tone = ChipTone.Neutral)
                ConnectionState.Connected -> StatusChip(label = "Connected", tone = ChipTone.Success)
            }
        }
    }
}

@Composable
fun WizardList(content: @Composable () -> Unit) {
    Div(attrs = { classes("sb-wiz-list") }) { content() }
}

@Composable
fun TickRow(text: String, done: Boolean = true) {
    Div(attrs = { classes("sb-wiz-row") }) {
        Div(attrs = { classes("sb-wiz-tick"); if (done) classes("sb-wiz-tick--done") }) {
            if (done) LucideCheck(size = 10)
        }
        Span { Text(text) }
    }
}

@Composable
fun FactRow(label: String, value: String, icon: @Composable () -> Unit) {
    Div(attrs = { classes("sb-wiz-row") }) {
        Div(attrs = { classes("sb-wiz-opt-lead") }) { icon() }
        Span { Text(label) }
        Span(attrs = { classes("sb-wiz-row-value") }) { Text(value) }
    }
}
